"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { GlowCard } from "@/components/crystality/glow-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  Loader2, 
  Check, 
  X, 
  Copy, 
  ChevronLeft, 
  Clock, 
  ExternalLink,
  RefreshCw,
  Search,
  AlertCircle,
} from "lucide-react"
import { cn } from "@/lib/utils"
import QRCode from "qrcode"

interface CryptoCheckoutProps {
  email: string
  planId: string
  planName: string
  priceGbp: number
  referralCode?: string
  onComplete: (licenseKey: string, orderId: string) => void
  onCancel: () => void
  onError: (error: string) => void
}

interface Currency {
  id: string
  name: string
  color: string
  popular: boolean
}

interface PaymentDetails {
  orderId: string
  paymentId: string
  payAddress: string
  payAmount: number
  payCurrency: string
  priceAmount: number
  priceCurrency: string
  cryptoName: string
  network?: string
  expiresAt?: string
  status: string
}

type CheckoutStep = "select-currency" | "payment" | "confirming" | "success" | "failed" | "expired"

const POLLING_INTERVAL = 3000 // 3 seconds
const MAX_POLLING_TIME = 30 * 60 * 1000 // 30 minutes

export function CryptoCheckout({
  email,
  planId,
  planName,
  priceGbp,
  referralCode,
  onComplete,
  onCancel,
  onError,
}: CryptoCheckoutProps) {
  const [step, setStep] = useState<CheckoutStep>("select-currency")
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(true)
  const [isCreatingPayment, setIsCreatingPayment] = useState(false)
  const [payment, setPayment] = useState<PaymentDetails | null>(null)
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState<"address" | "amount" | null>(null)
  const [error, setError] = useState("")
  const [estimatedAmount, setEstimatedAmount] = useState<number | null>(null)
  const [isLoadingEstimate, setIsLoadingEstimate] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [lastChecked, setLastChecked] = useState<Date | null>(null)
  const [checkCount, setCheckCount] = useState(0)
  
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const elapsedTimerRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number>(0)

  // Fetch available currencies on mount
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetch("/api/checkout/crypto/currencies")
        if (!response.ok) throw new Error("Failed to fetch currencies")
        
        const data = await response.json()
        setCurrencies(data.currencies || [])
      } catch (err) {
        setError("Failed to load payment methods. Please try again.")
        onError("Failed to load currencies")
      } finally {
        setIsLoadingCurrencies(false)
      }
    }
    
    fetchCurrencies()
  }, [onError])

  // Fetch estimate when currency is selected
  useEffect(() => {
    if (!selectedCurrency) {
      setEstimatedAmount(null)
      return
    }

    const fetchEstimate = async () => {
      setIsLoadingEstimate(true)
      try {
        const response = await fetch(
          `/api/checkout/crypto/estimate?amount=${priceGbp}&currency=${selectedCurrency}`
        )
        if (response.ok) {
          const data = await response.json()
          setEstimatedAmount(data.estimatedAmount)
        }
      } catch (err) {
        console.error("Failed to fetch estimate:", err)
      } finally {
        setIsLoadingEstimate(false)
      }
    }

    fetchEstimate()
  }, [selectedCurrency, priceGbp])

  // Generate QR code when payment is created
  useEffect(() => {
    if (payment?.payAddress) {
      // Generate QR code with the payment address
      // Format: cryptocurrency:address?amount=X
      const qrData = payment.payCurrency === "btc" 
        ? `bitcoin:${payment.payAddress}?amount=${payment.payAmount}`
        : payment.payAddress
      
      QRCode.toDataURL(qrData, {
        width: 200,
        margin: 2,
        color: {
          dark: "#ffffff",
          light: "#00000000",
        },
      }).then(setQrCodeUrl).catch(console.error)
    }
  }, [payment])

  // Poll for payment status
  const pollPaymentStatus = useCallback(async () => {
    if (!payment?.orderId || !payment?.paymentId) return

    // Check if we've exceeded max polling time
    if (Date.now() - startTimeRef.current > MAX_POLLING_TIME) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      setStep("expired")
      return
    }

    try {
      setCheckCount(prev => prev + 1)
      setLastChecked(new Date())
      
      const response = await fetch(
        `/api/checkout/crypto/status?orderId=${encodeURIComponent(payment.orderId)}&paymentId=${encodeURIComponent(payment.paymentId)}`
      )
      
      if (!response.ok) return

      const data = await response.json()

      if (data.status === "completed" || data.status === "finished") {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        setStep("success")
        setTimeout(() => {
          onComplete(data.licenseKey || "", payment.orderId)
        }, 2000)
        return
      }

      if (data.status === "failed" || data.status === "refunded") {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        setStep("failed")
        return
      }

      if (data.status === "expired") {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        setStep("expired")
        return
      }

      // Update status if confirming
      if (data.nowPaymentsStatus === "confirming" || data.nowPaymentsStatus === "confirmed") {
        setStep("confirming")
      }
    } catch (err) {
      console.error("Polling error:", err)
    }
  }, [payment, onComplete])

  // Start polling when payment is created
  useEffect(() => {
    if ((step === "payment" || step === "confirming") && payment && !pollingRef.current) {
      startTimeRef.current = Date.now()
      setElapsedTime(0)
      setCheckCount(0)
      pollingRef.current = setInterval(pollPaymentStatus, POLLING_INTERVAL)
      // Also poll immediately
      pollPaymentStatus()
      
      // Start elapsed time counter
      elapsedTimerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000))
      }, 1000)
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current)
        elapsedTimerRef.current = null
      }
    }
  }, [step, payment, pollPaymentStatus])

  // Format elapsed time
  const formatElapsedTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Create payment
  const handleCreatePayment = async () => {
    if (!selectedCurrency) return

    setIsCreatingPayment(true)
    setError("")

    try {
      const response = await fetch("/api/checkout/crypto/payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          planId,
          payCurrency: selectedCurrency,
          price: priceGbp,
          referralCode,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to create payment")
      }

      const data = await response.json()
      setPayment({
        orderId: data.orderId,
        paymentId: data.paymentId,
        payAddress: data.payAddress,
        payAmount: data.payAmount,
        payCurrency: data.payCurrency,
        priceAmount: data.priceAmount,
        priceCurrency: data.priceCurrency,
        cryptoName: data.cryptoName,
        network: data.network,
        expiresAt: data.expiresAt,
        status: data.status,
      })
      setStep("payment")
    } catch (err: any) {
      setError(err.message || "Failed to create payment")
      onError(err.message)
    } finally {
      setIsCreatingPayment(false)
    }
  }

  // Copy to clipboard
  const handleCopy = async (text: string, type: "address" | "amount") => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  // Filter currencies by search
  const filteredCurrencies = currencies.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.id.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const popularCurrencies = filteredCurrencies.filter(c => c.popular)
  const otherCurrencies = filteredCurrencies.filter(c => !c.popular)

  // Currency selection step
  if (step === "select-currency") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Select Cryptocurrency</h1>
          <p className="mt-2 text-muted-foreground">
            Choose which crypto you want to pay with
          </p>
        </div>

        {/* Order Summary */}
        <GlowCard variant="subtle" className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{planName}</p>
              <p className="text-xs text-muted-foreground">{email}</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold">£{priceGbp.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">GBP</p>
            </div>
          </div>
        </GlowCard>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search cryptocurrencies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-secondary/50"
          />
        </div>

        {isLoadingCurrencies ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1 scrollbar-custom">
            {/* Popular currencies */}
            {popularCurrencies.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Popular</p>
                <div className="grid grid-cols-2 gap-2">
                  {popularCurrencies.map((currency) => (
                    <button
                      key={currency.id}
                      onClick={() => setSelectedCurrency(currency.id)}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                        selectedCurrency === currency.id
                          ? "border-primary bg-primary/10"
                          : "border-border bg-secondary/30 hover:bg-secondary/50"
                      )}
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
                        style={{ backgroundColor: currency.color }}
                      >
                        {currency.id.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{currency.name}</p>
                        <p className="text-xs text-muted-foreground uppercase">{currency.id}</p>
                      </div>
                      {selectedCurrency === currency.id && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Other currencies */}
            {otherCurrencies.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">All Currencies</p>
                <div className="grid grid-cols-2 gap-2">
                  {otherCurrencies.slice(0, 20).map((currency) => (
                    <button
                      key={currency.id}
                      onClick={() => setSelectedCurrency(currency.id)}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                        selectedCurrency === currency.id
                          ? "border-primary bg-primary/10"
                          : "border-border bg-secondary/30 hover:bg-secondary/50"
                      )}
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs bg-muted-foreground"
                      >
                        {currency.id.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{currency.name}</p>
                        <p className="text-xs text-muted-foreground uppercase">{currency.id}</p>
                      </div>
                    </button>
                  ))}
                </div>
                {otherCurrencies.length > 20 && (
                  <p className="text-xs text-muted-foreground text-center">
                    +{otherCurrencies.length - 20} more currencies available
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Estimated amount */}
        {selectedCurrency && (
          <GlowCard variant="subtle" className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Estimated amount</p>
              {isLoadingEstimate ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : estimatedAmount ? (
                <p className="font-mono font-medium">
                  ~{Number(estimatedAmount).toFixed(8)} {selectedCurrency?.toUpperCase()}
                </p>
              ) : (
                <p className="text-muted-foreground">-</p>
              )}
            </div>
          </GlowCard>
        )}

        {error && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            <p>{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onCancel}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            className="flex-1 gradient-purple text-white"
            onClick={handleCreatePayment}
            disabled={!selectedCurrency || isCreatingPayment}
          >
            {isCreatingPayment ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </div>
    )
  }

  // Payment step (waiting for payment)
  if (step === "payment" || step === "confirming") {
    return (
      <div className="space-y-6">
        {/* Live Status Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-xs font-medium text-green-500">LIVE</span>
            <span className="text-xs text-muted-foreground">• Auto-updating</span>
          </div>
          
          <h1 className="text-2xl font-bold">
            {step === "confirming" ? "Confirming Payment..." : "Send Payment"}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {step === "confirming" 
              ? "Your payment has been detected. Waiting for blockchain confirmations."
              : `Send exactly ${payment?.payAmount} ${payment?.payCurrency?.toUpperCase()} to the address below`}
          </p>
        </div>

        {/* QR Code */}
        <div className="flex justify-center">
          <GlowCard variant="featured" className="p-6 inline-block relative">
            {qrCodeUrl ? (
              <img 
                src={qrCodeUrl} 
                alt="Payment QR Code" 
                className="w-48 h-48 mx-auto"
              />
            ) : (
              <div className="w-48 h-48 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
          </GlowCard>
        </div>

        {/* Payment Details */}
        <div className="space-y-4">
          {/* Amount */}
          <GlowCard variant="subtle" className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Amount to send</p>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => handleCopy(String(payment?.payAmount), "amount")}
              >
                {copied === "amount" ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="font-mono text-xl font-bold text-primary">
              {payment?.payAmount} {payment?.payCurrency?.toUpperCase()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              ≈ £{payment?.priceAmount?.toFixed(2)} GBP
            </p>
          </GlowCard>

          {/* Address */}
          <GlowCard variant="subtle" className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">
                {payment?.cryptoName} Address
                {payment?.network && <span className="ml-1">({payment.network})</span>}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => handleCopy(payment?.payAddress || "", "address")}
              >
                {copied === "address" ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="font-mono text-sm break-all">
              {payment?.payAddress}
            </p>
          </GlowCard>

          {/* Status */}
          <GlowCard 
            variant={step === "confirming" ? "featured" : "subtle"} 
            className="p-4"
            glow={step === "confirming"}
          >
            <div className="flex items-center gap-3">
              {step === "confirming" ? (
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <RefreshCw className="h-5 w-5 text-primary animate-spin" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center animate-pulse">
                  <Clock className="h-5 w-5 text-yellow-500" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">
                    {step === "confirming" ? "Payment Detected" : "Waiting for Payment"}
                  </p>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span className="inline-block w-1 h-1 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="inline-block w-1 h-1 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }}></span>
                    <span className="inline-block w-1 h-1 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }}></span>
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {step === "confirming" 
                    ? "Transaction is being confirmed on the blockchain"
                    : "This page updates automatically — no refresh needed"}
                </p>
              </div>
            </div>
            
            {/* Real-time stats */}
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatElapsedTime(elapsedTime)}
              </span>
              {lastChecked && (
                <span className="flex items-center gap-1 text-green-500">
                  <RefreshCw className={cn("h-3 w-3", checkCount > 0 && "animate-spin")} style={{ animationDuration: "1s" }} />
                  Last checked: just now
                </span>
              )}
            </div>
          </GlowCard>
        </div>

        {/* Warning */}
        <div className="flex items-start gap-2 text-xs bg-secondary/50 rounded-lg p-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-yellow-500" />
          <div>
            <p className="font-medium text-foreground">Important:</p>
            <ul className="list-disc list-inside mt-1 space-y-1 text-muted-foreground">
              <li>Send the <span className="text-foreground font-medium">exact amount</span> shown above</li>
              <li>Only send {payment?.payCurrency?.toUpperCase()} to this address</li>
              <li>Payment will be confirmed after blockchain confirmations</li>
            </ul>
          </div>
        </div>

        {/* Network Fee Warning */}
        <div className="flex items-start gap-2 text-xs bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-destructive" />
          <div>
            <p className="font-medium text-destructive">Network Fees:</p>
            <p className="mt-1 text-muted-foreground">
              You are responsible for covering blockchain network fees. Ensure the <span className="text-foreground font-medium">full amount reaches this address</span> after fees. 
              We are not responsible for underpayments and <span className="text-foreground font-medium">no refunds</span> will be issued for incomplete transactions.
            </p>
          </div>
        </div>

        {/* Cancel */}
        <Button
          variant="outline"
          className="w-full"
          onClick={onCancel}
        >
          Cancel Payment
        </Button>
      </div>
    )
  }

  // Success step
  if (step === "success") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center animate-in zoom-in-50 duration-300">
            <Check className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-green-500">Payment Successful!</h1>
          <p className="mt-2 text-muted-foreground">
            Your payment has been confirmed. Setting up your account...
          </p>
        </div>
      </div>
    )
  }

  // Failed step
  if (step === "failed") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-destructive/20 flex items-center justify-center">
            <X className="h-10 w-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold">Payment Failed</h1>
          <p className="mt-2 text-muted-foreground">
            Something went wrong with your payment. Please try again.
          </p>
        </div>
        <Button
          className="w-full gradient-purple text-white"
          onClick={() => {
            setStep("select-currency")
            setPayment(null)
            setSelectedCurrency(null)
          }}
        >
          Try Again
        </Button>
        <Button variant="outline" className="w-full" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    )
  }

  // Expired step
  if (step === "expired") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <Clock className="h-10 w-10 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold">Payment Expired</h1>
          <p className="mt-2 text-muted-foreground">
            The payment window has expired. Please create a new payment.
          </p>
        </div>
        <Button
          className="w-full gradient-purple text-white"
          onClick={() => {
            setStep("select-currency")
            setPayment(null)
            setSelectedCurrency(null)
          }}
        >
          Create New Payment
        </Button>
        <Button variant="outline" className="w-full" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    )
  }

  return null
}
